import React, { useEffect, useState, useRef, useCallback } from 'react';
import TruncatedText from './TruncatedText';
import FeedbackPanel from './FeedbackPanel';
import ChangeReasonPrompt from './ChangeReasonPrompt';
import AiLearningPrompt from './AiLearningPrompt';
import AiLearningProgress from './AiLearningProgress';
import AiEditPopup from './AiEditPopup';
import ScoreScale from './ScoreScale';
import StyleTag from './StyleTag';
import useGradeRatings from './useGradeRatings';
import useAiRegrade from './useAiRegrade';
import useAiLearning from './useAiLearning';
import { gradeApi } from '../../../api.js';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

const GradingRubric = ({
  examPaper,
  parentQuestion, // Added to accept parent question data directly
  regradeInProgress,
  pinnedSubQuestionId,
  togglePinSubQuestion,
  handleRubricItemChange,
  getFeedback,
  getInstructorFeedback,
  isEditing,
  handleRemoveFeedback,
  handleEditFeedback,
  handleFeedbackChange,
  handleFeedbackSave,
  handleFeedbackCancel,
  regradeRef,
  getRR,
  handleRegradeClick,
  questionIndex, // Optional - will be used if provided
  subQuestionIndex, // Optional - will be used if provided
  subQuestion, // Optional - will be used if provided
  assignment_id,
  user,
  setMessage,
  getQuestions,
  getAssignmentSubmission,
  regrade_question_id, // Added back the props reference for regrade_question_id
  sortedSubmissionIds,
  submission_id,
  getRRText,
  handleRegradeReq,
  updateExamPaper, // Added this prop
  uploadQuestions // Added this prop
}) => {
  const currentSubmissionIndex = sortedSubmissionIds
    ? sortedSubmissionIds.indexOf(Number(submission_id))
    : -1;
  const remainingSubmissionIds =
    currentSubmissionIndex >= 0 && sortedSubmissionIds
      ? sortedSubmissionIds.slice(currentSubmissionIndex)
      : [];
  const remainingCount = remainingSubmissionIds.length - 1;

  // Use custom hooks
  const {
    gradeRatings,
    showThumbsDownMessage,
    showChangeReasonPrompt,
    changeReasons,
    savingRating,
    showAiLearningPrompt,
    aiLearningLoading,
    handleThumbsUp,
    handleThumbsDown,
    handleManualGradeChange,
    handleChangeReasonSubmit,
    handleChangeReasonSkip,
    handleReasonChange,
    triggerAiLearning,
    handleAiLearningDismiss
  } = useGradeRatings(examPaper, getFeedback, submission_id, assignment_id, user);

  const {
    showAiEditPopup,
    aiEditInput,
    isProcessingAiEdit,
    activeSubQuestion,
    editingSolutionId,
    editedSolutions,
    editingCriteriaId,
    changedMaxWeights,
    setEditingCriteriaId,
    setChangedMaxWeights,
    handleAiEditClick,
    enhance_single_rubric,
    toggleSolutionEditor,
    handleSaveSolution,
    handleSolutionChange,
    handleAIRegrade,
    startPollingForRegradeCompletion,
    handleAiEditPopupCancel,
    handleAiEditInputChange
  } = useAiRegrade(assignment_id, user, getQuestions, getAssignmentSubmission);

  // Add AI Learning hook for better polling
  const {
    aiLearningInProgress,
    triggerAiLearningWithPolling,
    checkExistingLearningFromQuestions
  } = useAiLearning(assignment_id, user, getQuestions, getAssignmentSubmission);

  // Monitor changes to regradeInProgress to start polling
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      Object.entries(regradeInProgress).forEach(([subquestionId, isRegrading]) => {
        if (isRegrading) {
          startPollingForRegradeCompletion(subquestionId);
        }
      });
    }, 5000); // Small debounce

    return () => clearTimeout(timeoutId);
  }, [regradeInProgress, startPollingForRegradeCompletion]);

  // Check for existing AI learning processes when questions data loads
  useEffect(() => {
    if (examPaper && examPaper.length > 0) {
      checkExistingLearningFromQuestions(examPaper);
    }
  }, [examPaper, checkExistingLearningFromQuestions]);

  // State to track rendered questions for animations
  const [renderedQuestions, setRenderedQuestions] = useState([]);
  const [renderedSubQuestions, setRenderedSubQuestions] = useState({});

  // Effect to update rendered questions for animations
  useEffect(() => {
    // Track which questions and subquestions should be visible
    if (pinnedSubQuestionId) {
      // If something is pinned, only keep track of questions containing the pinned subquestion
      const newRenderedQuestions = examPaper
        .filter(q => q.subQuestions.some(sq => sq.id === pinnedSubQuestionId))
        .map(q => q.id);

      // For subquestions, only keep the pinned one
      const newRenderedSubQuestions = {};
      newRenderedQuestions.forEach(qId => {
        newRenderedSubQuestions[qId] = [pinnedSubQuestionId];
      });

      setRenderedQuestions(newRenderedQuestions);
      setRenderedSubQuestions(newRenderedSubQuestions);
    } else {
      // If nothing is pinned, all questions and subquestions are visible
      setRenderedQuestions(examPaper.map(q => q.id));

      const allSubQuestions = {};
      examPaper.forEach(q => {
        allSubQuestions[q.id] = q.subQuestions.map(sq => sq.id);
      });
      setRenderedSubQuestions(allSubQuestions);
    }
  }, [pinnedSubQuestionId, examPaper]);

  // Add a ref for the textarea to maintain focus
  const textareaRef = useRef(null);

  // Main rendering
  return (
    <>
      <StyleTag />
      {/* Only render directly from examPaper when no specific subQuestion is provided */}
      {!subQuestion &&
        examPaper.map((question, qIndex) => {
          const isVisible = renderedQuestions.includes(question.id);
          const shouldRender = pinnedSubQuestionId
            ? question.subQuestions.some(sq => sq.id === pinnedSubQuestionId)
            : true;

          if (!shouldRender && !isVisible) {
            return null;
          }

          return (
            <div
              key={qIndex}
              className="mb-8 transition-all duration-300 ease-in-out overflow-visible"
              style={{
                opacity: isVisible ? '1' : '0',
                height: isVisible ? 'auto' : '0',
                marginBottom: isVisible ? '2rem' : '0'
              }}
            >
              {question.subQuestions.map((sq, sqIndex) => {
                // Use the mapped one since subQuestion prop is not provided here
                const currentSubQuestion = sq;
                // Use the array indices
                const currentQuestionIndex = qIndex;
                const currentSubQuestionIndex = sqIndex;

                const isSubVisible = renderedSubQuestions[question.id]?.includes(
                  currentSubQuestion.id
                );
                const shouldRenderSub = pinnedSubQuestionId
                  ? currentSubQuestion.id === pinnedSubQuestionId
                  : true;

                if (!shouldRenderSub && !isSubVisible) {
                  return null;
                }

                const isDisabledGrading = false; // Removed complex check for disabled grading
                const itemProps =
                  currentSubQuestion.id === regrade_question_id ? { ref: regradeRef } : {};

                return (
                  <div
                    key={sqIndex}
                    id={`subquestion-${currentSubQuestion.id}`}
                    className={`mb-6 ml-4 transition-all duration-300 ease-in-out relative ${regradeInProgress[currentSubQuestion.id] ? 'opacity-80 cursor-not-allowed pointer-events-none' : ''}`}
                    style={{
                      opacity: isSubVisible ? '1' : '0',
                      height: isSubVisible ? 'auto' : '0',
                      marginBottom: isSubVisible ? '1.5rem' : '0',
                      transform:
                        pinnedSubQuestionId === currentSubQuestion.id ? 'scale(1.02)' : 'scale(1)',
                      padding: pinnedSubQuestionId === currentSubQuestion.id ? '1rem' : '0',
                      borderRadius: pinnedSubQuestionId === currentSubQuestion.id ? '0.5rem' : '0'
                    }}
                    {...itemProps}
                    data-subquestion-id={currentSubQuestion.id}
                  >
                    {regradeInProgress[currentSubQuestion.id] && (
                      <>
                        <div className="mt-2 mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-700 rounded">
                          ⏳ This subquestion is being regraded...
                        </div>
                        {/* Add an overlay to visually indicate the rubric is not interactable */}
                        <div
                          className="absolute inset-0 bg-gray-100 bg-opacity-50 z-10 rounded-lg flex items-center justify-center"
                          style={{ pointerEvents: 'none' }}
                        ></div>
                      </>
                    )}

                    {/* Question header */}
                    <div className="mb-3">
                      {/* Question header styling - simple styling approach */}
                      {
                        <>
                          {/* Main question with text on the same line */}
                          {sqIndex === 0 && (
                            <div className="mb-4">
                              <div className="flex items-baseline justify-between mb-1">
                                <div className="flex items-baseline flex-grow">
                                  <TruncatedText
                                    text={question.description}
                                    className="text-gray-700 font-medium text-lg"
                                    maxLength={70}
                                  />
                                </div>
                                {/* Pin button moved to overarching question level when no subquestions */}
                                {question.subQuestions.length === 1 &&
                                  !currentSubQuestion.question && (
                                    <button
                                      style={{
                                        transition: 'all 0.3s ease',
                                        transform:
                                          pinnedSubQuestionId === currentSubQuestion.id
                                            ? 'scale(1.1)'
                                            : 'scale(1)',
                                        boxShadow:
                                          pinnedSubQuestionId === currentSubQuestion.id
                                            ? '0 4px 6px rgba(0, 0, 0, 0.1)'
                                            : 'none'
                                      }}
                                      className={`inline-flex items-center px-4 py-2 rounded-full ${pinnedSubQuestionId === currentSubQuestion.id ? 'bg-[#e4b17a] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                      title={
                                        pinnedSubQuestionId === currentSubQuestion.id
                                          ? 'Unpin this subquestion'
                                          : 'Pin this subquestion'
                                      }
                                      onClick={e => {
                                        e.stopPropagation();
                                        // Flash animation effect
                                        const element = document.getElementById(
                                          `subquestion-${currentSubQuestion.id}`
                                        );
                                        if (
                                          element &&
                                          pinnedSubQuestionId !== currentSubQuestion.id
                                        ) {
                                          // Apply flash effect
                                          element.style.transition = 'all 0.5s ease';
                                          setTimeout(() => {
                                            if (pinnedSubQuestionId !== currentSubQuestion.id) {
                                              element.style.backgroundColor = '';
                                            }
                                          }, 500);
                                        }
                                        togglePinSubQuestion(currentSubQuestion.id);
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M19 3.5l-3 2V11c0 1.2.5 2.3 1.4 3.3l.6.6V21l-6-1l-6 1v-6.1l.6-.6c.9-1 1.4-2.1 1.4-3.3V5.5l-3-2 2-2h10l2 2z" />
                                      </svg>
                                      <span className="text-sm font-medium">
                                        {pinnedSubQuestionId === currentSubQuestion.id
                                          ? 'Unpin'
                                          : 'Pin'}
                                      </span>
                                    </button>
                                  )}
                              </div>
                            </div>
                          )}
                          {/* Subquestion with consistent styling - check if it's the only subquestion with blank text */}
                          <div className="mb-3">
                            <div className="flex items-baseline justify-between mb-1">
                              <div className="flex items-baseline flex-grow">
                                {/* If there's only one subquestion with blank text, don't show the subquestion letter */}
                                <span className="text-gray-700 mr-2">
                                  {question.subQuestions.length === 1 &&
                                  !currentSubQuestion.question
                                    ? ``
                                    : `${currentQuestionIndex + 1}${String.fromCharCode(97 + currentSubQuestionIndex)}) `}
                                </span>
                                <TruncatedText
                                  text={currentSubQuestion.question}
                                  className="text-gray-700"
                                  maxLength={70}
                                />
                              </div>
                              {/* Only show pin button here if not already shown at the question level */}
                              {!(
                                question.subQuestions.length === 1 && !currentSubQuestion.question
                              ) && (
                                <button
                                  style={{
                                    transition: 'all 0.3s ease',
                                    transform:
                                      pinnedSubQuestionId === currentSubQuestion.id
                                        ? 'scale(1.1)'
                                        : 'scale(1)',
                                    boxShadow:
                                      pinnedSubQuestionId === currentSubQuestion.id
                                        ? '0 4px 6px rgba(0, 0, 0, 0.1)'
                                        : 'none'
                                  }}
                                  className={`inline-flex items-center px-4 py-2 rounded-full ${pinnedSubQuestionId === currentSubQuestion.id ? 'bg-[#e4b17a] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                  title={
                                    pinnedSubQuestionId === currentSubQuestion.id
                                      ? 'Unpin this subquestion'
                                      : 'Pin this subquestion'
                                  }
                                  onClick={e => {
                                    e.stopPropagation();
                                    // Flash animation effect
                                    const element = document.getElementById(
                                      `subquestion-${currentSubQuestion.id}`
                                    );
                                    if (element && pinnedSubQuestionId !== currentSubQuestion.id) {
                                      // Apply flash effect
                                      element.style.transition = 'all 0.5s ease';
                                      setTimeout(() => {
                                        if (pinnedSubQuestionId !== currentSubQuestion.id) {
                                          element.style.backgroundColor = '';
                                        }
                                      }, 500);
                                    }
                                    togglePinSubQuestion(currentSubQuestion.id);
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path d="M19 3.5l-3 2V11c0 1.2.5 2.3 1.4 3.3l.6.6V21l-6-1l-6 1v-6.1l.6-.6c.9-1 1.4-2.1 1.4-3.3V5.5l-3-2 2-2h10l2 2z" />
                                  </svg>
                                  <span className="text-sm font-medium">
                                    {pinnedSubQuestionId === currentSubQuestion.id
                                      ? 'Unpin'
                                      : 'Pin'}
                                  </span>
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      }
                      {/* Pin button has been moved to be inline with question above */}
                      {/* Essay question styling */}
                    </div>

                    {/* Score scale section with fine-tune button */}
                    <div className="flex flex-col gap-2 mb-2 mt-3 relative">
                      {/* Inline Solution Editor - moved above the scale */}
                      {editingSolutionId === currentSubQuestion.id && (
                        <div
                          className="w-full mb-2 animate-solution-fade-in solution-editor"
                          style={{
                            background: 'rgb(255, 255, 255)',
                            borderRadius: '1rem',
                            border: '1px solid rgba(228, 177, 122, 0.12)',
                            boxShadow:
                              'rgba(228, 177, 122, 0.06) 0px 2px 4px -2px, rgba(228, 177, 122, 0.04) 0px 4px 8px -2px',
                            padding: '1rem',
                            zIndex: 1000,
                            isolation: 'isolate'
                          }}
                        >
                          <style jsx>{`
                            @keyframes solutionFadeIn {
                              0% {
                                opacity: 0;
                                transform: translateY(10px);
                              }
                              100% {
                                opacity: 1;
                                transform: translateY(0px);
                              }
                            }
                          `}</style>
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="text-base font-medium text-[#8c6b4a]">
                              Edit Grading Criteria
                            </h3>
                            <div className="flex items-center gap-3">
                              <button
                                className="edit-ai-btn"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '5px 10px',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  background: 'linear-gradient(to right, #e4b17a, #c99b69)',
                                  border: 'none',
                                  fontWeight: '500',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                }}
                                title="Enhance with AI"
                                onClick={() => handleAiEditClick(currentSubQuestion)}
                              >
                                <Sparkles
                                  size={18}
                                  style={{ marginRight: '6px', color: '#fff', flexShrink: 0 }}
                                />
                                Edit with AI
                              </button>

                              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-md shadow-sm">
                                <button
                                  className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded-l-md focus:outline-none transition-colors"
                                  title="Decrease max score by 0.5"
                                  onClick={() => {
                                    if (editingSolutionId === currentSubQuestion.id) {
                                      const element = document.getElementById(
                                        `subquestion-${currentSubQuestion.id}`
                                      );
                                      let currentScore = parseFloat(
                                        currentSubQuestion.max_weight || 1
                                      );
                                      if (element) {
                                        const dataMaxWeight =
                                          element.getAttribute('data-max-weight');
                                        if (dataMaxWeight) {
                                          currentScore = parseFloat(dataMaxWeight);
                                        }
                                      }
                                      const newScore = Math.max(
                                        0,
                                        parseFloat((currentScore - 0.5).toFixed(1))
                                      );
                                      if (element) {
                                        element.setAttribute(
                                          'data-max-weight',
                                          newScore.toString()
                                        );
                                      }
                                      const originalMaxWeight = currentSubQuestion.max_weight || 1;
                                      const hasChanged =
                                        Math.abs(newScore - originalMaxWeight) > 0.001;
                                      setChangedMaxWeights(prev => ({
                                        ...prev,
                                        [currentSubQuestion.id]: hasChanged
                                      }));
                                      setRenderedQuestions([...renderedQuestions]);
                                    }
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>

                                <div className="flex items-center px-2">
                                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap mr-1">
                                    Max:
                                  </span>
                                  <span
                                    className="inline-block w-10 text-center font-medium text-gray-800"
                                    style={{ minWidth: '2.5rem' }}
                                  >
                                    {(() => {
                                      const element = document.getElementById(
                                        `subquestion-${currentSubQuestion.id}`
                                      );
                                      let value;
                                      if (element) {
                                        const dataMaxWeight =
                                          element.getAttribute('data-max-weight');
                                        if (dataMaxWeight) {
                                          value = parseFloat(dataMaxWeight);
                                        } else {
                                          value = parseFloat(currentSubQuestion.max_weight || 1);
                                        }
                                      } else {
                                        value = parseFloat(currentSubQuestion.max_weight || 1);
                                      }
                                      if (value === 0) {
                                        return '0';
                                      }
                                      return value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
                                    })()}
                                  </span>
                                </div>

                                <button
                                  className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded-r-md focus:outline-none transition-colors"
                                  title="Increase max score by 0.5"
                                  onClick={() => {
                                    if (editingSolutionId === currentSubQuestion.id) {
                                      const element = document.getElementById(
                                        `subquestion-${currentSubQuestion.id}`
                                      );
                                      let currentScore = parseFloat(
                                        currentSubQuestion.max_weight || 1
                                      );
                                      if (element) {
                                        const dataMaxWeight =
                                          element.getAttribute('data-max-weight');
                                        if (dataMaxWeight) {
                                          currentScore = parseFloat(dataMaxWeight);
                                        }
                                      }
                                      const newScore = Math.min(
                                        20,
                                        parseFloat((currentScore + 0.5).toFixed(1))
                                      );
                                      if (element) {
                                        element.setAttribute(
                                          'data-max-weight',
                                          newScore.toString()
                                        );
                                      }
                                      const originalMaxWeight = currentSubQuestion.max_weight || 1;
                                      const hasChanged =
                                        Math.abs(newScore - originalMaxWeight) > 0.001;
                                      setChangedMaxWeights(prev => ({
                                        ...prev,
                                        [currentSubQuestion.id]: hasChanged
                                      }));
                                      setRenderedQuestions([...renderedQuestions]);
                                    }
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 01-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>

                          <textarea
                            ref={textareaRef}
                            id={`textarea-${currentSubQuestion.id}`}
                            className="w-full p-3 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white text-gray-700 text-sm"
                            style={{
                              minHeight: '100px',
                              resize: 'vertical'
                            }}
                            value={editedSolutions[currentSubQuestion.id] || ''}
                            placeholder="Enter the solution here..."
                            onChange={e => {
                              // Auto-resize the textarea based on content
                              const textarea = e.target;
                              textarea.style.height = 'auto';
                              textarea.style.height = textarea.scrollHeight + 'px';
                              handleSolutionChange(currentSubQuestion.id, e.target.value);
                            }}
                            onClick={() => setEditingCriteriaId(currentSubQuestion.id)}
                            onFocus={e => {
                              setEditingCriteriaId(currentSubQuestion.id);
                              // Auto-resize on focus too
                              const textarea = e.target;
                              textarea.style.height = 'auto';
                              textarea.style.height = textarea.scrollHeight + 'px';
                            }}
                          />
                          <div className="flex justify-end space-x-2 mt-3">
                            <button
                              className="px-3 py-1 bg-gray-50 text-gray-600 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors text-sm"
                              onClick={() => {
                                // Reset the data-max-weight attribute to the original value when canceling
                                const element = document.getElementById(
                                  `subquestion-${editingSolutionId}`
                                );
                                if (element) {
                                  // Find the original max_weight from the subquestion object
                                  const subQ = examPaper
                                    .flatMap(q => q.subQuestions)
                                    .find(sq => sq.id === editingSolutionId);
                                  const originalMaxWeight = subQ?.max_weight || 1;
                                  // Reset the data attribute
                                  element.setAttribute(
                                    'data-max-weight',
                                    originalMaxWeight.toString()
                                  );

                                  // Force re-render to show the updated value
                                  setRenderedQuestions([...renderedQuestions]);
                                }

                                // Reset changed max weights for this subquestion
                                setChangedMaxWeights(prev => ({
                                  ...prev,
                                  [editingSolutionId]: false
                                }));

                                // Close the editor
                                toggleSolutionEditor(editingSolutionId, examPaper);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="px-3 py-1.5 flex items-center gap-1 text-white rounded-md text-sm hover:transform hover:-translate-y-[1px] transition-all duration-300 hover:shadow-[0_8px_24px_rgba(228,177,122,0.3)]"
                              style={{
                                background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                                boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)'
                              }}
                              onClick={() => handleAIRegrade(editingSolutionId, examPaper)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-1"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M7 16l-4-4 4-4" />
                                <path d="M17 8l4 4-4 4" />
                                <path d="M3 12h18" />
                              </svg>
                              Regrade All
                            </button>
                            {sortedSubmissionIds && remainingCount > 0 && (
                              <button
                                className={`px-3 py-1.5 flex items-center gap-1 text-white rounded-md text-sm hover:transform hover:-translate-y-[1px] transition-all duration-300 hover:shadow-[0_8px_24px_rgba(228,177,122,0.3)] ${
                                  changedMaxWeights[editingSolutionId]
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                                }`}
                                style={{
                                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                                  boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)'
                                }}
                                disabled={changedMaxWeights[editingSolutionId]}
                                title={
                                  changedMaxWeights[editingSolutionId]
                                    ? 'Regrade Next is disabled when max points have been changed'
                                    : `Regrade the next ${remainingCount} submissions`
                                }
                                onClick={() =>
                                  !changedMaxWeights[editingSolutionId] &&
                                  handleSaveSolution(editingSolutionId, remainingSubmissionIds, examPaper)
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 mr-1"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M5 12h14" />
                                  <path d="M12 5l7 7-7 7" />
                                </svg>
                                This + Next {remainingCount}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Fine Tune Grading button - top-right of scale */}
                      <div className="absolute top-0 right-0 z-10">
                        <button
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-500 font-normal text-xs transition-all duration-300 hover:bg-gray-100 hover:text-gray-600"
                          onClick={() => toggleSolutionEditor(currentSubQuestion.id, examPaper)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-3 h-3"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                          </svg>
                          <span>
                            {editingSolutionId === currentSubQuestion.id ? 'Close' : 'Fine Tune'}
                          </span>
                        </button>
                      </div>


                      <ScoreScale
                        subQuestion={currentSubQuestion}
                        gradeRatings={gradeRatings}
                        showThumbsDownMessage={showThumbsDownMessage}
                        handleThumbsUp={handleThumbsUp}
                        handleThumbsDown={handleThumbsDown}
                        handleManualGradeChange={handleManualGradeChange}
                        getFeedback={getFeedback}
                        handleRubricItemChange={handleRubricItemChange}
                        editingSolutionId={editingSolutionId}
                        setEditingCriteriaId={setEditingCriteriaId}
                        setChangedMaxWeights={setChangedMaxWeights}
                        renderedQuestions={renderedQuestions}
                        setRenderedQuestions={setRenderedQuestions}
                        showChangeReasonPrompt={showChangeReasonPrompt}
                        changeReasons={changeReasons}
                        handleReasonChange={handleReasonChange}
                        handleChangeReasonSubmit={handleChangeReasonSubmit}
                        handleChangeReasonSkip={handleChangeReasonSkip}
                        showAiLearningPrompt={showAiLearningPrompt}
                        aiLearningLoading={aiLearningLoading}
                        triggerAiLearning={triggerAiLearning}
                        handleAiLearningDismiss={handleAiLearningDismiss}
                        aiLearningInProgress={aiLearningInProgress}
                        triggerAiLearningWithPolling={triggerAiLearningWithPolling}
                      />
                    </div>

                    {/* Feedback section - handled by separate FeedbackPanel */}
                    <div className="mt-4">
                      {!isEditing(currentSubQuestion.id) && (
                        <FeedbackPanel
                          key={`feedback-${submission_id}-${currentSubQuestion.id}`}
                          subQuestion={currentSubQuestion}
                          getFeedback={getFeedback}
                          handleRemoveFeedback={handleRemoveFeedback}
                          handleEditFeedback={handleEditFeedback}
                          handleFeedbackChange={handleFeedbackChange}
                          handleFeedbackSave={handleFeedbackSave}
                          handleFeedbackCancel={handleFeedbackCancel}
                          disabled={aiLearningInProgress[currentSubQuestion.id]}
                        />
                      )}
                    </div>

                    {/* Regrade request UI */}
                    {getRR && getRR(currentSubQuestion.id) && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-[rgba(228,177,121,0.08)] to-[rgba(228,177,121,0.04)] border-l-4 border-[#e4b17a] rounded-lg transition-all duration-200 hover:shadow-sm">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-[#8c6b4a] mb-2 flex items-center gap-2">
                            <ExclamationCircleIcon className="w-5 h-5 text-[#e4b17a]" />
                            Regrade Request
                          </h4>
                          <button
                            className="px-3 py-1 bg-white text-[#c99b69] border border-[#e4b17a] rounded-md hover:bg-[#fdf7f1] hover:text-[#b38757] transition-colors font-medium flex items-center gap-1 shadow-sm"
                            onClick={() => handleRegradeReq(currentSubQuestion.id)}
                          >
                            <XMarkIcon className="w-4 h-4" />
                            Dismiss
                          </button>
                        </div>
                        <p className="text-[#a17c57]">
                          {getRRText && getRRText(currentSubQuestion.id)}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      {/* When a specific subQuestion is provided, only render that one */}
      {subQuestion && (
        <div className="mb-8 transition-all duration-300 ease-in-out overflow-visible">
          {Object.keys(subQuestion).includes('question') && (
            <pre className="hidden">{JSON.stringify(subQuestion, null, 2)}</pre>
          )}
          <div
            ref={subQuestion.id === regrade_question_id ? regradeRef : null}
            id={`subquestion-${subQuestion.id}`}
            className={`mb-6 ml-4 transition-all duration-300 ease-in-out relative ${regradeInProgress[subQuestion.id] ? 'opacity-80 cursor-not-allowed pointer-events-none' : ''}`}
            style={{
              opacity: 1,
              height: 'auto',
              marginBottom: '1.5rem',
              transform: pinnedSubQuestionId === subQuestion.id ? 'scale(1.02)' : 'scale(1)',
              padding: pinnedSubQuestionId === subQuestion.id ? '1rem' : '0',
              borderRadius: pinnedSubQuestionId === subQuestion.id ? '0.5rem' : '0'
            }}
            data-subquestion-id={subQuestion.id}
          >
            {regradeInProgress[subQuestion.id] && (
              <>
                <div className="mt-2 mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-700 rounded">
                  ⏳ This subquestion is being regraded...
                </div>
                {/* Add an overlay to visually indicate the rubric is not interactable */}
                <div
                  className="absolute inset-0 bg-gray-100 bg-opacity-50 z-10 rounded-lg flex items-center justify-center"
                  style={{ pointerEvents: 'none' }}
                ></div>
              </>
            )}

            {/* Question header */}
            <div className="mb-3">
              {/* Question header styling - simple styling approach */}
              {
                <>
                  {/* Main question with text on the same line */}
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between mb-1">
                      <div className="flex items-baseline flex-grow">
                        <span className="text-gray-700 mr-2 font-medium text-lg">{`${questionIndex !== undefined ? questionIndex + 1 : '?'}) `}</span>
                        <TruncatedText
                          text={
                            examPaper.find(q => q.subQuestions.some(sq => sq.id === subQuestion.id))
                              ?.description || 'Question'
                          }
                          className="text-gray-700 font-medium text-lg"
                          maxLength={70}
                        />
                      </div>

                      {/* Pin button for this specific subquestion */}
                      <button
                        className={`transition-all ml-2 p-1 rounded-full ${
                          pinnedSubQuestionId === subQuestion.id
                            ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                            : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'
                        }`}
                        title={
                          pinnedSubQuestionId === subQuestion.id ? 'Unpin question' : 'Pin question'
                        }
                        onClick={() => togglePinSubQuestion(subQuestion.id)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M19 3.5l-3 2V11c0 1.2.5 2.3 1.4 3.3l.6.6V21l-6-1l-6 1v-6.1l.6-.6c.9-1 1.4-2.1 1.4-3.3V5.5l-3-2 2-2h10l2 2z" />
                        </svg>
                        <span className="text-sm font-medium">
                          {pinnedSubQuestionId === subQuestion.id ? 'Unpin' : 'Pin'}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Subquestion display with the identifier */}
                  <div className="flex items-start mb-2 mt-0">
                    <span className="text-gray-700 font-medium mr-2">
                      {`${String.fromCharCode(97 + (subQuestionIndex !== undefined ? subQuestionIndex : 0)).toUpperCase()}.`}
                    </span>
                    <div className="flex-1 text-gray-600">
                      <TruncatedText
                        text={
                          subQuestion.question || subQuestion.text || subQuestion.description || ''
                        }
                        maxLength={200}
                      />
                    </div>
                  </div>
                </>
              }
            </div>

            <div className="relative">

                          <ScoreScale
              subQuestion={subQuestion}
              gradeRatings={gradeRatings}
              showThumbsDownMessage={showThumbsDownMessage}
              handleThumbsUp={handleThumbsUp}
              handleThumbsDown={handleThumbsDown}
              handleManualGradeChange={handleManualGradeChange}
              getFeedback={getFeedback}
              handleRubricItemChange={handleRubricItemChange}
              editingSolutionId={editingSolutionId}
              setEditingCriteriaId={setEditingCriteriaId}
              setChangedMaxWeights={setChangedMaxWeights}
              renderedQuestions={renderedQuestions}
              setRenderedQuestions={setRenderedQuestions}
              showChangeReasonPrompt={showChangeReasonPrompt}
              changeReasons={changeReasons}
              handleReasonChange={handleReasonChange}
              handleChangeReasonSubmit={handleChangeReasonSubmit}
              handleChangeReasonSkip={handleChangeReasonSkip}
              showAiLearningPrompt={showAiLearningPrompt}
              aiLearningLoading={aiLearningLoading}
              triggerAiLearning={triggerAiLearning}
              handleAiLearningDismiss={handleAiLearningDismiss}
              aiLearningInProgress={aiLearningInProgress}
              triggerAiLearningWithPolling={triggerAiLearningWithPolling}
            />

          </div>

            {/* Feedback section - Add this to match the other rendering path */}
            <div className="mt-4">
              {!isEditing(subQuestion.id) && (
                <FeedbackPanel
                  key={`feedback-${submission_id}-${subQuestion.id}`}
                  subQuestion={subQuestion}
                  getFeedback={getFeedback}
                  handleRemoveFeedback={handleRemoveFeedback}
                  handleEditFeedback={handleEditFeedback}
                  handleFeedbackChange={handleFeedbackChange}
                  handleFeedbackSave={handleFeedbackSave}
                  handleFeedbackCancel={handleFeedbackCancel}
                  disabled={aiLearningInProgress[subQuestion.id]}
                />
              )}
            </div>

            {/* Regrade request UI */}
            {getRR && getRR(subQuestion.id) && (
              <div className="mt-4 p-4 bg-gradient-to-r from-[rgba(228,177,121,0.08)] to-[rgba(228,177,121,0.04)] border-l-4 border-[#e4b17a] rounded-lg transition-all duration-200 hover:shadow-sm">
                <div className="flex justify-between items-start">
                  <h4 className="font-medium text-[#8c6b4a] mb-2 flex items-center gap-2">
                    <ExclamationCircleIcon className="w-5 h-5 text-[#e4b17a]" />
                    Regrade Request
                  </h4>
                  <button
                    className="px-3 py-1 bg-white text-[#c99b69] border border-[#e4b17a] rounded-md hover:bg-[#fdf7f1] hover:text-[#b38757] transition-colors font-medium flex items-center gap-1 shadow-sm"
                    onClick={() => handleRegradeReq(subQuestion.id)}
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Dismiss
                  </button>
                </div>
                <p className="text-[#a17c57]">{getRRText && getRRText(subQuestion.id)}</p>
              </div>
            )}
          </div>
        </div>
      )}
      <AiEditPopup
        isVisible={showAiEditPopup}
        inputValue={aiEditInput}
        onInputChange={handleAiEditInputChange}
        onSubmit={() => enhance_single_rubric(examPaper, updateExamPaper, uploadQuestions)}
        onCancel={handleAiEditPopupCancel}
        isProcessing={isProcessingAiEdit}
      />
    </>
  );
};

export default GradingRubric;
